import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";

export const GuestTermsList = ({ terms = {}, compact = false }) => {
  const rows = [["Covered by the circle", terms.covered], ["Not covered (personal extras)", terms.excluded], ["Still undecided", terms.undecided]];
  return (
    <dl className={compact ? "space-y-3" : "space-y-5"} data-testid="guest-terms-list">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="text-sm font-semibold">{k}</dt>
          <dd className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{v || "Not set yet"}</dd>
        </div>
      ))}
    </dl>
  );
};

export const RequestDialog = ({ open, onOpenChange, exp, kind, onDone, requestId }) => {
  const [message, setMessage] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const isGuest = kind === "guest";
  const accepting = !!requestId;

  const submit = async () => {
    if (isGuest && !ack) return toast.error("Please acknowledge the guest terms");
    setBusy(true);
    try {
      const { data } = accepting
        ? await api.post(`/requests/${requestId}/respond`, { accept: true, acknowledge_terms: ack })
        : await api.post(`/experiences/${exp.id}/requests`, { kind, message, acknowledge_terms: ack });
      toast.success(accepting ? "Invitation accepted" : "Request sent to the lead host");
      onOpenChange(false);
      onDone?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-lg" data-testid="request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">{accepting ? "Accept invitation" : isGuest ? "Request a guest place" : "Request to co-host"}</DialogTitle>
          <DialogDescription>{exp.title} · {exp.destination}</DialogDescription>
        </DialogHeader>
        {isGuest && (
          <div className="rounded-lg border border-border bg-background/60 p-4">
            <p className="mb-3 text-sm font-semibold text-primary">Guest terms · version {exp.terms_version}</p>
            <GuestTermsList terms={exp.guest_terms} compact />
          </div>
        )}
        {!isGuest && <p className="text-sm text-muted-foreground">Co-hosts agree their own contribution individually with the circle. Nothing is committed until you confirm your share.</p>}
        {!accepting && (
          <div>
            <label htmlFor="req-msg" className="field-label">Message to the lead host <span className="text-muted-foreground">(optional)</span></label>
            <textarea id="req-msg" rows={4} maxLength={600} className="field py-3" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="request-message-input" placeholder="Introduce yourself and what you'd bring to the plan." />
          </div>
        )}
        {isGuest && (
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} className="mt-0.5 h-5 w-5" data-testid="request-ack-checkbox" />
            I have reviewed what is covered, what is not, and what is still undecided.
          </label>
        )}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" className="btn-outline" onClick={() => onOpenChange(false)} data-testid="request-cancel-button">Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy} data-testid="request-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : accepting ? "Accept" : "Send request"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">Planning only. Bookings and payments are arranged outside the platform.</p>
      </DialogContent>
    </Dialog>
  );
};
